import React, { useEffect, useState } from "react"
import { useParams } from "react-router"
import { useTranslation } from "react-i18next"
import {
  Plus,
  X,
  ChevronDown,
  BookOpen,
  PersonStanding,
  Globe,
  ListChecks,
  AlertTriangle,
  Brain,
  ImageIcon,
  Send,
  CreditCard,
  Volume2,
  FileText,
  TableProperties,
} from "lucide-react"

import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Input,
  Label,
  Switch,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"
import { useAgentHistories } from "@/hooks/useAgentHistories"
import {
  transformText2SpeechModelOption,
  transformText2SpeechVoiceOption,
  useGetText2SpeechModelsApi,
  useGetText2SpeechVoicesApi,
} from "@/hooks/api/agents"
import { AgentHistory } from "@/types/agent"
import { useToast } from "@/hooks"
import { FormEditorModal } from "@/components/FormBuilder/FormEditorModal"
import type { RequestFormSchema } from "@/types/requestForm"
import { useStore } from "@/store"

import { AgentHistoryDropdownBase } from "@/components/Agent"

import { KnowledgeBaseToolSection } from "./KnowledgeBaseToolSection"
import { TabularDataToolSection } from "./TabularDataToolSection"
import { InvocationInstructionField } from "./InvocationInstructionField"
import { HttpRequestToolModal } from "./HttpRequestToolModal"
import { Text2SpeechToolSection } from "./Text2SpeechToolSection"
import { UnifiedTool, UnifiedToolSelectorProps } from "./type"
import type { HttpRequestToolConfig } from "@/types/httpRequestTool"
import { useKnowledgeBaseTabularCapabilitiesApi } from "@/hooks/api"

// HTTP tools historically persisted the localized type label as their name.
// Treat those values as defaults so switching locales does not expose them as
// a redundant custom-name badge.
const defaultHttpRequestNames = new Set([
  "HTTP Request",
  "HTTP 請求",
  "HTTP リクエスト",
])

// Simple wrapper to call the hook
const AgentVersionDropdownWrapper = ({
  agentId,
  disabled,
  toolId,
  initialVersion,
  onVersionChange,
  hidden = false,
}: {
  agentId: string
  disabled: boolean
  toolId: string
  initialVersion?: number
  onVersionChange?: (toolId: string, history: AgentHistory) => void
  hidden?: boolean
}) => {
  const { t } = useTranslation()

  // Use the hook with initial version (skipInitialFetch removed)
  const historiesState = useAgentHistories(agentId, {
    initialVersion: initialVersion !== -1 ? initialVersion : undefined,
  })

  // Notify parent when current history changes
  React.useEffect(() => {
    if (historiesState.currentHistory && onVersionChange) {
      onVersionChange(toolId, historiesState.currentHistory)
    }
  }, [historiesState.currentHistory, toolId, onVersionChange])

  const dropdownTrigger = (currentHistory: AgentHistory | null) => (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      className="h-8 px-3 gap-2"
    >
      <span className="text-sm font-medium">
        {currentHistory
          ? t("workspace.agent.history.ui.versionNumber", {
              number: currentHistory.versionNumber,
            })
          : ""}
        {/* because the "Loading versions..." is too long and it cause the dropdown to switch to a new line.
          So show empty string instead. */}
      </span>
      <ChevronDown className="h-3 w-3" />
    </Button>
  )

  // If hidden, don't render the dropdown UI but still return null
  // The hook will still run and fetch data, calling onVersionChange
  if (hidden) {
    return null
  }

  return (
    <AgentHistoryDropdownBase
      agentId={agentId}
      disabled={disabled}
      state={historiesState}
      showPagination={true}
      renderTrigger={dropdownTrigger}
    />
  )
}

// Constants for tool limits
const MAX_TOTAL_TOOLS = 10
const MAX_CALL_AGENT_TOOLS = 5

const DEFAULT_HTTP_REQUEST_CONFIG: HttpRequestToolConfig = {
  method: "GET",
  urlTemplate: "https://",
  auth: { type: "none" },
  headers: [],
  queryParams: [],
  body: { template: undefined },
}

// Supports validating single or comma-separated list of domains.
const validateDomain = (input: string): boolean => {
  if (!input || !input.trim()) return true // optional field

  // Split by comma for multi-domain support
  const domains = input
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean)

  if (domains.length === 0) return true

  const isValidHost = (raw: string): boolean => {
    // Reject spaces inside a single domain part
    if (raw.includes(" ")) return false

    try {
      // Ensure protocol for URL parsing
      const url = new URL(
        raw.startsWith("http://") || raw.startsWith("https://")
          ? raw
          : `https://${raw}`
      )
      const { hostname } = url
      if (!hostname) return false

      // Allowed chars
      if (!/^[a-zA-Z0-9.-]+$/.test(hostname)) return false

      // Structural rules
      if (
        hostname.startsWith(".") ||
        hostname.startsWith("-") ||
        hostname.endsWith(".") ||
        hostname.endsWith("-") ||
        hostname.includes("..")
      )
        return false

      return hostname.length > 0
    } catch {
      return false
    }
  }

  return domains.every(isValidHost)
}

export const UnifiedToolSelector = ({
  knowledgeBases,
  agents,
  selectedTools,
  onToolsChange,
  disabled = false,
  pendingKnowledgeBindings = [],
  onPendingKnowledgeBindingsChange,
  onTabularToolsEnabledChange,
}: UnifiedToolSelectorProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const availableImageModels = useStore((s) => s.availableImageModels) ?? []
  const organizationId = useStore(
    (state: { currentOrganization: { id?: string } | null }) =>
      state.currentOrganization?.id
  )
  const { workspaceId } = useParams()
  const { fetchFn: fetchTabularCapabilities } =
    useKnowledgeBaseTabularCapabilitiesApi()
  const [tabularToolsEnabled, setTabularToolsEnabled] = useState(false)
  const {
    data: text2SpeechModelsResponse,
    error: text2SpeechModelsError,
    status: text2SpeechModelsStatus,
    fetchFn: fetchText2SpeechModels,
  } = useGetText2SpeechModelsApi()
  const {
    data: text2SpeechVoicesResponse,
    error: text2SpeechVoicesError,
    status: text2SpeechVoicesStatus,
    fetchFn: fetchText2SpeechVoices,
  } = useGetText2SpeechVoicesApi()
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null)
  const [domainErrors, setDomainErrors] = useState<Record<string, boolean>>({})
  const [invalidToolMap, setInvalidToolMap] = useState<Record<string, boolean>>(
    {}
  )

  useEffect(() => {
    if (!organizationId || !workspaceId) {
      setTabularToolsEnabled(false)
      onTabularToolsEnabledChange?.(false)
      return
    }
    let active = true
    fetchTabularCapabilities({
      urlBuilderParams: { organizationId, workspaceId },
    })
      .then((response) => {
        if (active) {
          const enabled = response?.data?.enabled === true
          setTabularToolsEnabled(enabled)
          onTabularToolsEnabledChange?.(enabled)
        }
      })
      .catch((error) => {
        console.error("Failed to load tabular tool capability", error)
        if (active) {
          setTabularToolsEnabled(false)
          onTabularToolsEnabledChange?.(false)
        }
      })
    return () => {
      active = false
    }
  }, [
    fetchTabularCapabilities,
    onTabularToolsEnabledChange,
    organizationId,
    workspaceId,
  ])

  // Track current agent_history(agent version) for each call_agent tool
  const [toolAgentVersions, setToolAgentVersions] = useState<
    Record<string, AgentHistory>
  >({})

  // Helper function to render warning tooltip for no published version
  const renderNoPublishedVersionTooltip = (toolId: string) => {
    const tool = selectedTools.find((t) => t.id === toolId)
    if (
      !tool ||
      tool.version !== -1 ||
      !toolAgentVersions[toolId] ||
      toolAgentVersions[toolId].wasPublished !== false
    ) {
      return null
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="h-3 w-3 text-yellow-500" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="text-sm">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.noPublishedVersionTooltip"
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Check for deleted agents and filter them out
  useEffect(() => {
    const agentIds = new Set(agents.map((agent) => agent.id))
    const deletedAgentTools = selectedTools.filter(
      (tool) =>
        tool.type === "call_agent" &&
        tool.agentId &&
        !agentIds.has(tool.agentId)
    )

    if (deletedAgentTools.length > 0) {
      // Filter out deleted agent tools
      const filteredTools = selectedTools.filter(
        (tool) =>
          !(
            tool.type === "call_agent" &&
            tool.agentId &&
            !agentIds.has(tool.agentId)
          )
      )
      onToolsChange(filteredTools)

      // Show error toast for deleted agents
      const deletedAgentNames = deletedAgentTools
        .map(
          (tool) =>
            tool.name ||
            t("workspace.agent.basicForm.unifiedToolSelector.unknownAgent")
        )
        .join(", ")

      alertToast({
        title: t(
          "workspace.agent.basicForm.unifiedToolSelector.deletedAgentTitle"
        ),
        description: t(
          "workspace.agent.basicForm.unifiedToolSelector.deletedAgentDescription",
          {
            names: deletedAgentNames,
          }
        ),
      })
    }
  }, [agents, selectedTools, onToolsChange, alertToast, t])

  // Calculate current tool counts
  const totalToolsCount = selectedTools.length
  const callAgentToolsCount = selectedTools.filter(
    (tool) => tool.type === "call_agent"
  ).length
  const hasMemoryTool = selectedTools.some((tool) => tool.type === "memory")
  const hasText2SpeechTool = selectedTools.some(
    (tool) => tool.type === "text2speech"
  )

  // Check if limits are reached
  const isMaxTotalReached = totalToolsCount >= MAX_TOTAL_TOOLS
  const isMaxCallAgentReached = callAgentToolsCount >= MAX_CALL_AGENT_TOOLS
  const isMaxMemoryReached = hasMemoryTool

  useEffect(() => {
    if (!workspaceId || !hasText2SpeechTool) return

    const modelSearchParams = new URLSearchParams({
      workspace_id: workspaceId,
    })
    const voiceSearchParams = new URLSearchParams({
      workspace_id: workspaceId,
      page_size: "100",
    })

    fetchText2SpeechModels({ searchParams: modelSearchParams }).catch(() => {
      return undefined
    })
    fetchText2SpeechVoices({ searchParams: voiceSearchParams }).catch(() => {
      return undefined
    })
  }, [
    fetchText2SpeechModels,
    fetchText2SpeechVoices,
    hasText2SpeechTool,
    workspaceId,
  ])

  const handleAddTool = (tool: Omit<UnifiedTool, "id">) => {
    if (tool.type === "memory" && hasMemoryTool) {
      alertToast({
        title: t(
          "workspace.agent.basicForm.unifiedToolSelector.memoryToolAlreadyAdded"
        ),
        description: t(
          "workspace.agent.basicForm.unifiedToolSelector.memoryToolAlreadyAddedDescription"
        ),
      })
      return
    }
    const newTool: UnifiedTool = {
      ...tool,
      id: `${tool.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }
    onToolsChange([...selectedTools, newTool])
  }

  const handleRemoveTool = (toolId: string) => {
    onToolsChange(selectedTools.filter((tool) => tool.id !== toolId))
    onPendingKnowledgeBindingsChange?.(
      pendingKnowledgeBindings.filter((binding) => binding.tool_id !== toolId)
    )
    setInvalidToolMap((prev) => {
      if (!(toolId in prev)) return prev
      const next = { ...prev }
      delete next[toolId]
      return next
    })
  }

  const handleRemovePendingKnowledgeFile = (
    tool: UnifiedTool,
    fileId: number
  ) => {
    const nextBindings = pendingKnowledgeBindings
      .map((binding) =>
        binding.tool_id === tool.id
          ? {
              ...binding,
              files: binding.files.filter((file) => file.id !== fileId),
            }
          : binding
      )
      .filter((binding) => binding.files.length > 0)
    onPendingKnowledgeBindingsChange?.(nextBindings)
    if (
      !tool.knowledgeNodeIds?.length &&
      !nextBindings.some((binding) => binding.tool_id === tool.id)
    ) {
      onToolsChange(
        selectedTools.filter((candidate) => candidate.id !== tool.id)
      )
    }
  }

  const handleUpdateTool = React.useCallback(
    (toolId: string, updates: Partial<UnifiedTool>) => {
      onToolsChange(
        selectedTools.map((tool) =>
          tool.id === toolId ? { ...tool, ...updates } : tool
        )
      )
    },
    [selectedTools, onToolsChange]
  )

  const handleInvalidSelectionChange = React.useCallback(
    (toolId: string, hasInvalid: boolean) => {
      setInvalidToolMap((prev) => {
        if (prev[toolId] === hasInvalid) return prev
        return { ...prev, [toolId]: hasInvalid }
      })
    },
    []
  )

  const toggleExpanded = (toolId: string) => {
    setExpandedToolId(expandedToolId === toolId ? null : toolId)
  }

  // Handle agent_history(agent version) changes for call_agent tools
  const handleAgentVersionChange = React.useCallback(
    (toolId: string, history: AgentHistory) => {
      setToolAgentVersions((prev) => ({
        ...prev,
        [toolId]: history,
      }))

      // Only update the tool's version if we're not in "latest" mode
      // Find the tool to check if it's using latest version
      const tool = selectedTools.find((t) => t.id === toolId)
      if (
        tool &&
        tool.version !== -1 &&
        history?.versionNumber &&
        tool.version !== history.versionNumber
      ) {
        handleUpdateTool(toolId, { version: history.versionNumber })
      }
    },
    [handleUpdateTool, selectedTools]
  )

  // Get selected tool identifiers for filtering
  const selectedAgentIds = selectedTools
    .filter((tool) => tool.type === "call_agent")
    .map((tool) => tool.agentId)
    .filter(Boolean)

  // Filter options based on search and selection status
  const hasKnowledgeBases = knowledgeBases.length > 0
  const filteredAgents = agents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchValue.toLowerCase()) &&
      !selectedAgentIds.includes(agent.id)
  )
  const defaultImageModel = availableImageModels[0]?.modelId ?? ""
  const text2SpeechModels = (text2SpeechModelsResponse?.data ?? []).map(
    transformText2SpeechModelOption
  )
  const text2SpeechVoices = (text2SpeechVoicesResponse?.data ?? []).map(
    transformText2SpeechVoiceOption
  )
  const text2SpeechModelsMessage = text2SpeechModelsResponse?.message
  const text2SpeechVoicesMessage = text2SpeechVoicesResponse?.message

  const getLocalizedToolTypeName = React.useCallback(
    (tool: UnifiedTool): string => {
      const nameByType: Partial<Record<UnifiedTool["type"], string>> = {
        knowledge_base: t(
          "workspace.agent.basicForm.unifiedToolSelector.knowledgeBases"
        ),
        web_search: t(
          "workspace.agent.basicForm.unifiedToolSelector.webSearchTool"
        ),
        call_agent: t(
          "workspace.agent.basicForm.unifiedToolSelector.agentTools"
        ),
        request_form: t(
          "workspace.agent.basicForm.unifiedToolSelector.requestForm"
        ),
        image_generation: t(
          "workspace.agent.basicForm.unifiedToolSelector.imageGeneration"
        ),
        payment: t("workspace.agent.basicForm.unifiedToolSelector.paymentTool"),
        http_request: t(
          "workspace.agent.basicForm.unifiedToolSelector.httpRequestTool"
        ),
        memory: t("workspace.agent.basicForm.unifiedToolSelector.memoryTool"),
        text2speech: t(
          "workspace.agent.basicForm.unifiedToolSelector.text2SpeechTool"
        ),
        tabular_data: t(
          "workspace.agent.basicForm.unifiedToolSelector.tabularDataTool"
        ),
      }

      return nameByType[tool.type] ?? tool.name ?? tool.type
    },
    [t]
  )

  return (
    <div className="space-y-4">
      {/* Selected Tools Display */}
      <div className="space-y-2">
        {selectedTools.map((tool) => {
          const isImageTool = tool.type === "image_generation"
          const hasImageModel =
            !!tool.imageModel && tool.imageModel.trim().length > 0
          const isImageModelMissing =
            isImageTool && (!hasImageModel || availableImageModels.length === 0)
          const selectedText2SpeechVoice =
            tool.type === "text2speech"
              ? text2SpeechVoices.find(
                  (voice) => voice.voiceId === tool.text2speech?.voiceId
                )
              : undefined
          const hasInvalidSelection =
            (invalidToolMap[tool.id] ?? false) || isImageModelMissing
          const toolCardTitle = getLocalizedToolTypeName(tool)
          const normalizedToolName = tool.name?.trim()
          const httpRequestName =
            tool.type === "http_request" &&
            normalizedToolName &&
            normalizedToolName !== toolCardTitle &&
            !defaultHttpRequestNames.has(normalizedToolName)
              ? normalizedToolName
              : undefined
          return (
            <Card
              key={tool.id}
              className={`py-0 border gap-0 ${
                hasInvalidSelection
                  ? "border-red-500 dark:border-red-500"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <CardHeader
                className="py-3 gap-0 rounded-xl cursor-pointer transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => toggleExpanded(tool.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {tool.type === "knowledge_base" && (
                      <BookOpen className="h-4 w-4 text-blue-500" />
                    )}
                    {tool.type === "web_search" && (
                      <Globe className="h-4 w-4 text-green-500" />
                    )}
                    {tool.type === "call_agent" && (
                      <PersonStanding className="h-4 w-4 text-purple-500" />
                    )}
                    {tool.type === "request_form" && (
                      <ListChecks className="h-4 w-4 text-orange-500" />
                    )}
                    {tool.type === "memory" && (
                      <Brain className="h-4 w-4 text-cyan-500" />
                    )}
                    {tool.type === "image_generation" && (
                      <ImageIcon className="h-4 w-4 text-amber-500" />
                    )}
                    {tool.type === "payment" && (
                      <CreditCard className="h-4 w-4 text-amber-500" />
                    )}
                    {tool.type === "http_request" && (
                      <Send className="h-4 w-4 text-indigo-500" />
                    )}
                    {tool.type === "text2speech" && (
                      <Volume2 className="h-4 w-4 text-amber-600" />
                    )}
                    {tool.type === "tabular_data" && (
                      <TableProperties className="h-4 w-4 text-emerald-600" />
                    )}
                    <span className="min-w-0 break-words font-medium">
                      {toolCardTitle}
                    </span>
                    {httpRequestName && (
                      <Badge
                        variant="secondary"
                        className="max-w-full whitespace-normal break-words text-xs"
                      >
                        {httpRequestName}
                      </Badge>
                    )}
                    {tool.type === "call_agent" && (
                      <>
                        {(toolAgentVersions[tool.id]?.name || tool.name) && (
                          <Badge
                            variant="secondary"
                            className="max-w-full whitespace-normal break-words text-xs"
                          >
                            {toolAgentVersions[tool.id]?.name || tool.name}
                          </Badge>
                        )}
                        <Badge
                          variant="secondary"
                          className="max-w-full whitespace-normal break-words text-xs"
                        >
                          {tool.version === -1
                            ? t(
                                "workspace.agent.basicForm.unifiedToolSelector.usePublishedVersionLabel"
                              )
                            : `v${toolAgentVersions[tool.id]?.versionNumber}`}
                        </Badge>
                        {renderNoPublishedVersionTooltip(tool.id)}
                      </>
                    )}
                    {tool.type === "request_form" && (
                      <Badge
                        variant="secondary"
                        className="max-w-full whitespace-normal break-words text-xs"
                      >
                        {tool.customFormSchema?.title || "Untitled Form"}
                      </Badge>
                    )}
                    {tool.type === "image_generation" && (
                      <Badge
                        variant="secondary"
                        className="max-w-full whitespace-normal break-words text-xs"
                      >
                        {tool.imageModel ||
                          t(
                            "workspace.agent.basicForm.unifiedToolSelector.imageModelMissing"
                          )}
                      </Badge>
                    )}
                    {tool.type === "web_search" && tool.domain && (
                      <Badge
                        variant="secondary"
                        className="max-w-full whitespace-normal break-words text-xs"
                      >
                        {tool.domain}
                      </Badge>
                    )}
                    {tool.type === "http_request" &&
                      tool.httpRequest?.method && (
                        <Badge
                          variant="secondary"
                          className="max-w-full whitespace-normal break-words text-xs"
                        >
                          {tool.httpRequest.method}
                        </Badge>
                      )}
                    {tool.type === "http_request" && (
                      <Badge
                        variant={
                          tool.draftPolicy === "auto" ? "secondary" : "outline"
                        }
                        className="max-w-full whitespace-normal break-words text-xs"
                      >
                        {t(
                          tool.draftPolicy === "auto"
                            ? "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftAllowedBadge"
                            : "workspace.agent.basicForm.unifiedToolSelector.httpRequestAIDraftNotAllowedBadge"
                        )}
                      </Badge>
                    )}
                    {tool.type === "text2speech" && (
                      <>
                        <Badge
                          variant="secondary"
                          className="max-w-full whitespace-normal break-words text-xs"
                        >
                          {selectedText2SpeechVoice?.name ||
                            t(
                              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceRequired"
                            )}
                        </Badge>
                        {tool.text2speech?.modelId && (
                          <Badge
                            variant="outline"
                            className="max-w-full whitespace-normal break-words text-xs"
                          >
                            {tool.text2speech.modelId}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveTool(tool.id)
                      }}
                      disabled={disabled}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        expandedToolId === tool.id ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </div>
              </CardHeader>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedToolId === tool.id
                    ? "max-h-[1600px] opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <CardContent className="py-4">
                  <div className="space-y-3">
                    {/* Knowledge Base Details */}
                    {tool.type === "knowledge_base" && (
                      <>
                        <KnowledgeBaseToolSection
                          tool={tool}
                          disabled={disabled}
                          organizationId={organizationId}
                          workspaceId={workspaceId}
                          onUpdateTool={handleUpdateTool}
                          onInvalidSelectionChange={
                            handleInvalidSelectionChange
                          }
                        />
                        {pendingKnowledgeBindings
                          .filter((binding) => binding.tool_id === tool.id)
                          .flatMap((binding) => binding.files).length > 0 && (
                          <div className="space-y-2 rounded-lg border border-dashed p-3">
                            <p className="text-xs text-muted-foreground">
                              {t(
                                "workspace.agent.templates.knowledgeBase.pendingEditorHint"
                              )}
                            </p>
                            {pendingKnowledgeBindings
                              .filter((binding) => binding.tool_id === tool.id)
                              .flatMap((binding) => binding.files)
                              .map((file) => (
                                <div
                                  key={file.id}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                                  <span className="min-w-0 flex-1 truncate">
                                    {file.name}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-7"
                                    disabled={disabled}
                                    onClick={() =>
                                      handleRemovePendingKnowledgeFile(
                                        tool,
                                        file.id
                                      )
                                    }
                                    aria-label={t("common.remove")}
                                  >
                                    <X className="size-4" />
                                  </Button>
                                </div>
                              ))}
                          </div>
                        )}
                      </>
                    )}
                    {tool.type === "tabular_data" && (
                      <TabularDataToolSection
                        tool={tool}
                        disabled={disabled}
                        organizationId={organizationId}
                        workspaceId={workspaceId}
                        onUpdateTool={handleUpdateTool}
                        onInvalidSelectionChange={handleInvalidSelectionChange}
                      />
                    )}

                    {/* Web Search Details */}
                    {tool.type === "web_search" && (
                      <div className="space-y-3">
                        <InvocationInstructionField
                          id={`web-invocation-${tool.id}`}
                          placeholder={t(
                            "workspace.agent.basicForm.unifiedToolSelector.webSearchInvocationInstructionPlaceholder"
                          )}
                          value={tool.invocationInstruction}
                          onChange={(value) =>
                            handleUpdateTool(tool.id, {
                              invocationInstruction: value,
                            })
                          }
                          disabled={disabled}
                        />

                        <div className="space-y-2">
                          <Label
                            htmlFor={`domain-${tool.id}`}
                            className="text-sm font-medium"
                          >
                            {t(
                              "workspace.agent.basicForm.unifiedToolSelector.domain"
                            )}
                          </Label>
                          <Input
                            id={`domain-${tool.id}`}
                            placeholder={t(
                              "workspace.agent.basicForm.unifiedToolSelector.domainPlaceholder"
                            )}
                            value={tool.domain || ""}
                            onChange={(e) => {
                              const newDomain = e.target.value
                              handleUpdateTool(tool.id, {
                                domain: newDomain,
                              })
                              // Validate domain
                              const isValid = validateDomain(newDomain)
                              setDomainErrors((prev) => ({
                                ...prev,
                                [tool.id]: !isValid,
                              }))
                            }}
                            disabled={disabled}
                            className={`text-sm ${domainErrors[tool.id] ? "border-red-500 dark:border-red-800 focus-visible:ring-red-500 dark:focus-visible:ring-red-800" : ""}`}
                          />
                          {domainErrors[tool.id] && (
                            <p className="text-xs text-red-500 dark:text-red-900">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.domainError"
                              )}
                            </p>
                          )}
                          {!domainErrors[tool.id] && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.domainHelpText"
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Image Generation Details */}
                    {tool.type === "image_generation" && (
                      <div className="space-y-3">
                        <InvocationInstructionField
                          id={`image-invocation-${tool.id}`}
                          placeholder={t(
                            "workspace.agent.basicForm.unifiedToolSelector.imageGenerationInvocationInstructionPlaceholder"
                          )}
                          value={tool.invocationInstruction}
                          onChange={(value) =>
                            handleUpdateTool(tool.id, {
                              invocationInstruction: value,
                            })
                          }
                          disabled={disabled}
                        />

                        <div className="space-y-2">
                          <Label
                            htmlFor={`image-model-${tool.id}`}
                            className="text-sm font-medium"
                          >
                            {t(
                              "workspace.agent.basicForm.unifiedToolSelector.imageModelLabel"
                            )}
                          </Label>
                          <Select
                            value={tool.imageModel || ""}
                            onValueChange={(value) =>
                              handleUpdateTool(tool.id, {
                                imageModel: value,
                              })
                            }
                            disabled={
                              disabled || availableImageModels.length === 0
                            }
                          >
                            <SelectTrigger id={`image-model-${tool.id}`}>
                              <SelectValue
                                placeholder={t(
                                  "workspace.agent.basicForm.unifiedToolSelector.imageModelPlaceholder"
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {availableImageModels.map((model) => (
                                <SelectItem
                                  key={model.modelId}
                                  value={model.modelId}
                                >
                                  {model.displayName || model.modelId}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {availableImageModels.length === 0 ? (
                            <p className="text-xs text-red-500 dark:text-red-900">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.imageModelMissing"
                              )}
                            </p>
                          ) : !hasImageModel ? (
                            <p className="text-xs text-red-500 dark:text-red-900">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.imageModelRequired"
                              )}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.imageModelHelp"
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Agent Tool Details */}
                    {tool.type === "call_agent" && (
                      <div className="space-y-3">
                        <InvocationInstructionField
                          id={`agent-invocation-${tool.id}`}
                          placeholder={t(
                            "workspace.agent.basicForm.unifiedToolSelector.agentInvocationInstructionPlaceholder"
                          )}
                          value={tool.invocationInstruction}
                          onChange={(value) =>
                            handleUpdateTool(tool.id, {
                              invocationInstruction: value,
                            })
                          }
                          disabled={disabled}
                        />

                        <div>
                          <Label className="text-sm font-medium">
                            {t(
                              "workspace.agent.basicForm.unifiedToolSelector.agentDescription"
                            )}
                          </Label>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {toolAgentVersions[tool.id]?.description ||
                              tool.description ||
                              t(
                                "workspace.agent.basicForm.unifiedToolSelector.noDescriptionAvailable"
                              )}
                          </p>
                        </div>

                        <div className="space-y-4">
                          <Label className="text-sm font-medium">
                            {t(
                              "workspace.agent.basicForm.unifiedToolSelector.agentVersion"
                            )}
                          </Label>

                          {/* Compact Version Selection Panel */}
                          <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border dark:border-gray-700 transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <Switch
                                  id={`latest-${tool.id}`}
                                  checked={tool.version === -1}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      handleUpdateTool(tool.id, { version: -1 })
                                    } else {
                                      // When unchecking, set to version 1 as default
                                      handleUpdateTool(tool.id, { version: 1 })
                                    }
                                  }}
                                  disabled={disabled}
                                  className="transition-all duration-200"
                                />
                                <Label
                                  htmlFor={`latest-${tool.id}`}
                                  className="text-sm font-medium cursor-pointer transition-colors duration-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                                >
                                  {t(
                                    "workspace.agent.basicForm.unifiedToolSelector.usePublishedVersion"
                                  )}
                                  {renderNoPublishedVersionTooltip(tool.id)}
                                </Label>
                              </div>
                            </div>

                            {/* Version Dropdown - only when latest is not checked */}
                            {tool.agentId && (
                              <div className="space-y-2">
                                {tool.version !== -1 && (
                                  <Label className="text-sm text-gray-600 dark:text-gray-300">
                                    {t(
                                      "workspace.agent.basicForm.unifiedToolSelector.selectSpecificVersion"
                                    )}
                                  </Label>
                                )}
                                <AgentVersionDropdownWrapper
                                  agentId={tool.agentId}
                                  disabled={disabled}
                                  toolId={tool.id}
                                  initialVersion={tool.version}
                                  onVersionChange={handleAgentVersionChange}
                                  hidden={tool.version === -1}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {tool.type === "memory" && (
                      <InvocationInstructionField
                        label={t(
                          "workspace.agent.basicForm.unifiedToolSelector.memoryInstructionLabel"
                        )}
                        placeholder={t(
                          "workspace.agent.basicForm.unifiedToolSelector.memoryInstructionPlaceholder"
                        )}
                        value={tool.invocationInstruction}
                        onChange={(value) =>
                          handleUpdateTool(tool.id, {
                            invocationInstruction: value,
                          })
                        }
                        disabled={disabled}
                      />
                    )}
                    {tool.type === "payment" && (
                      <InvocationInstructionField
                        placeholder={t(
                          "workspace.agent.basicForm.unifiedToolSelector.paymentInvocationInstructionPlaceholder"
                        )}
                        value={tool.invocationInstruction}
                        onChange={(value) =>
                          handleUpdateTool(tool.id, {
                            invocationInstruction: value,
                          })
                        }
                        disabled={disabled}
                      />
                    )}
                    {/* Request Form - Modal */}
                    {tool.type === "request_form" && (
                      <div className="space-y-3">
                        <InvocationInstructionField
                          placeholder={t(
                            "workspace.agent.basicForm.unifiedToolSelector.requestFormInvocationInstructionPlaceholder"
                          )}
                          value={tool.invocationInstruction}
                          onChange={(value) =>
                            handleUpdateTool(tool.id, {
                              invocationInstruction: value,
                            })
                          }
                          disabled={disabled}
                        />
                        <Label className="text-sm font-medium block">
                          {t(
                            "workspace.agent.basicForm.unifiedToolSelector.formLabel"
                          )}
                        </Label>
                        <FormEditorModal
                          disabled={disabled}
                          schema={
                            tool.customFormSchema as
                              | RequestFormSchema
                              | undefined
                          }
                          onChange={(next) =>
                            handleUpdateTool(tool.id, {
                              customFormSchema: next,
                            })
                          }
                        />
                      </div>
                    )}
                    {tool.type === "http_request" && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              {tool.httpRequest?.method || "GET"}{" "}
                              {tool.httpRequest?.urlTemplate || "https://"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t(
                                "workspace.agent.basicForm.unifiedToolSelector.httpRequestSummary"
                              )}
                            </p>
                          </div>
                          <HttpRequestToolModal
                            tool={tool}
                            disabled={disabled}
                            onUpdateTool={handleUpdateTool}
                            onInvalidSelectionChange={
                              handleInvalidSelectionChange
                            }
                          />
                        </div>
                      </div>
                    )}
                    {tool.type === "text2speech" && (
                      <Text2SpeechToolSection
                        tool={tool}
                        disabled={disabled}
                        models={text2SpeechModels}
                        voices={text2SpeechVoices}
                        modelsStatus={text2SpeechModelsStatus}
                        voicesStatus={text2SpeechVoicesStatus}
                        modelsError={text2SpeechModelsError}
                        voicesError={text2SpeechVoicesError}
                        modelsMessage={text2SpeechModelsMessage}
                        voicesMessage={text2SpeechVoicesMessage}
                        onUpdateTool={handleUpdateTool}
                        onInvalidSelectionChange={handleInvalidSelectionChange}
                      />
                    )}
                  </div>
                </CardContent>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Add Tool Command Palette */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || isMaxTotalReached}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("workspace.agent.basicForm.unifiedToolSelector.addTool")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t(
                "workspace.agent.basicForm.unifiedToolSelector.searchTools"
              )}
              value={searchValue}
              onValueChange={setSearchValue}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.noToolsFound"
                )}
              </CommandEmpty>

              {/* Knowledge Bases */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.knowledgeBases"
                )}
              >
                {hasKnowledgeBases ? (
                  <CommandItem
                    onSelect={() => {
                      handleAddTool({
                        type: "knowledge_base",
                        name: t(
                          "workspace.agent.basicForm.unifiedToolSelector.knowledgeBases"
                        ),
                        knowledgeNodeIds: [],
                      })
                      setIsOpen(false)
                    }}
                    className="flex items-center gap-2"
                  >
                    <BookOpen className="h-4 w-4 text-blue-500" />
                    <span>
                      {t(
                        "workspace.agent.basicForm.unifiedToolSelector.knowledgeBases"
                      )}
                    </span>
                  </CommandItem>
                ) : (
                  <CommandItem
                    disabled
                    className="text-gray-500 dark:text-gray-400"
                  >
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.noToolsFound"
                    )}
                  </CommandItem>
                )}
              </CommandGroup>

              {tabularToolsEnabled && (
                <CommandGroup
                  heading={t(
                    "workspace.agent.basicForm.unifiedToolSelector.tabularData"
                  )}
                >
                  <CommandItem
                    onSelect={() => {
                      handleAddTool({
                        type: "tabular_data",
                        name: t(
                          "workspace.agent.basicForm.unifiedToolSelector.tabularDataTool"
                        ),
                      })
                      setIsOpen(false)
                    }}
                    className="flex items-center gap-2"
                  >
                    <TableProperties className="h-4 w-4 text-emerald-600" />
                    <span>
                      {t(
                        "workspace.agent.basicForm.unifiedToolSelector.tabularDataTool"
                      )}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}

              {/* Web Search */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.webSearch"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "web_search",
                      name: t(
                        "workspace.agent.basicForm.unifiedToolSelector.webSearchTool"
                      ),
                    })
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <Globe className="h-4 w-4 text-green-500" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.webSearchTool"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* Image Generation */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.imageGeneration"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "image_generation",
                      name: t(
                        "workspace.agent.basicForm.unifiedToolSelector.imageGeneration"
                      ),
                      imageModel: defaultImageModel || undefined,
                    })
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <ImageIcon className="h-4 w-4 text-amber-500" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.imageGeneration"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* Request Form */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.requestForm"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "request_form",
                      name: `${t("workspace.agent.basicForm.unifiedToolSelector.requestForm")}`,
                      customFormSchema: {
                        id: Math.random().toString(36).slice(2, 7),
                        title: t(
                          "workspace.agent.basicForm.formBuilder.preview.untitled",
                          { defaultValue: "Untitled Form" }
                        ),
                        fields: [],
                      },
                    } as Omit<UnifiedTool, "id">)
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <ListChecks className="h-4 w-4 text-orange-500" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.requestForm"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* Payment */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.paymentHeading"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "payment",
                      name: t(
                        "workspace.agent.basicForm.unifiedToolSelector.paymentTool"
                      ),
                    })
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <CreditCard className="h-4 w-4 text-amber-500" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.paymentTool"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* HTTP Request */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.httpRequest"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "http_request",
                      name: t(
                        "workspace.agent.basicForm.unifiedToolSelector.httpRequestTool"
                      ),
                      httpRequest: {
                        ...DEFAULT_HTTP_REQUEST_CONFIG,
                      },
                    })
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <Send className="h-4 w-4 text-indigo-500" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.httpRequestTool"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              {/* Memory */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2Speech"
                )}
              >
                <CommandItem
                  onSelect={() => {
                    handleAddTool({
                      type: "text2speech",
                      name: t(
                        "workspace.agent.basicForm.unifiedToolSelector.text2SpeechTool"
                      ),
                      invocationInstruction: t(
                        "workspace.agent.basicForm.unifiedToolSelector.text2SpeechInvocationInstructionDefault"
                      ),
                      text2speech: {
                        provider: "elevenlabs",
                        voiceId: "",
                        modelId: "eleven_multilingual_v2",
                        outputFormat: "mp3_44100_128",
                      },
                    })
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2"
                >
                  <Volume2 className="h-4 w-4 text-amber-600" />
                  <span>
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.text2SpeechTool"
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>

              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.memoryHeading"
                )}
              >
                {isMaxMemoryReached ? (
                  <CommandItem
                    disabled
                    className="text-gray-500 dark:text-gray-400"
                  >
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.memoryToolAlreadyAdded"
                    )}
                  </CommandItem>
                ) : (
                  <CommandItem
                    onSelect={() => {
                      handleAddTool({
                        type: "memory",
                        name: t(
                          "workspace.agent.basicForm.unifiedToolSelector.memoryTool"
                        ),
                      })
                      setIsOpen(false)
                    }}
                    className="flex items-center gap-2"
                  >
                    <Brain className="h-4 w-4 text-cyan-500" />
                    <span>
                      {t(
                        "workspace.agent.basicForm.unifiedToolSelector.memoryTool"
                      )}
                    </span>
                  </CommandItem>
                )}
              </CommandGroup>

              {/* Agent Tools */}
              <CommandGroup
                heading={t(
                  "workspace.agent.basicForm.unifiedToolSelector.agentTools"
                )}
              >
                {isMaxCallAgentReached ? (
                  <CommandItem
                    disabled
                    className="text-gray-500 dark:text-gray-400"
                  >
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.maxCallAgentReached"
                    )}
                  </CommandItem>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
                    <CommandItem
                      key={agent.id}
                      onSelect={() => {
                        handleAddTool({
                          type: "call_agent",
                          name: agent.name,
                          description: agent.description,
                          version: -1, // Default to latest version
                          agentId: agent.id,
                        })
                        setIsOpen(false)
                      }}
                      className="flex items-center gap-2"
                    >
                      <PersonStanding className="h-4 w-4 text-purple-500" />
                      <span>{agent.name}</span>
                    </CommandItem>
                  ))
                ) : (
                  <CommandItem
                    disabled
                    className="text-gray-500 dark:text-gray-400"
                  >
                    {t(
                      "workspace.agent.basicForm.unifiedToolSelector.noAgentToolsAvailable"
                    )}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
